import CompliancePage, {
  CompliancePageContent,
} from '@comp/compliance/compliance-template'

// TR-only page: every Localized field carries the same Turkish text in both slots.
const tr = (text: string) => ({ en: text, tr: text })

const content: CompliancePageContent = {
  path: '/compliance/siber-guvenlik-kanunu',
  bilingual: false,
  trOnly: true,
  seoTitle: tr('7545 Sayılı Siber Güvenlik Kanunu: Kimlik ve Erişim Hazırlığı | Monofor'),
  seoDescription: tr(
    '7545 sayılı Siber Güvenlik Kanunu kamu ve kritik altyapıya siber güvenlik tedbiri, olay bildirimi ve denetim yükümlülükleri getiriyor. Kimlik ve erişim tarafındaki hazırlığı Monofor ile bugünden kurun: MFA, ayrıcalıklı erişim, tedarikçi erişimi, iz kaydı.'
  ),
  heroEyebrow: tr('7545 Siber Güvenlik Kanunu'),
  heroTitle: tr('Denetim gelmeden erişim tarafını hazırlayın.'),
  heroBody: tr(
    '7545 sayılı Siber Güvenlik Kanunu; kamu kurumlarını ve kritik altyapı işletmecilerini siber güvenlik tedbirlerini almak, olayları bildirmek ve denetime hazır olmakla yükümlü kılıyor. İkincil mevzuat şekillenmeye devam ederken bir şey şimdiden kesin: kimlik doğrulama, ayrıcalıklı erişim ve iz kaydı her çerçevenin ortak paydası. Monofor bu kontrolleri bugünden uygular ve kanıtını üretir.'
  ),
  contextHeading: tr('Kanunun kimlik ve erişimle kesiştiği yer'),
  contextBody: tr(
    '7545 sayılı Siber Güvenlik Kanunu 2025 başında yürürlüğe girdi; Siber Güvenlik Başkanlığı çatısı altında kamu kurumları ve enerji, finans, haberleşme, sağlık, ulaştırma gibi kritik sektörlerdeki işletmeciler için siber güvenlik tedbiri alma, olayları bildirme ve denetimlere tabi olma yükümlülüklerini tek çerçevede topladı. Detayları belirleyecek ikincil mevzuat kademeli olarak yayımlanıyor; bu nedenle bu sayfa madde madde eşleme değil, hazırlık çerçevesi sunar.\n\nHangi yönetmelik hangi ayrıntıyı getirirse getirsin, denetimlerin erişim tarafında soracağı sorular dünyanın her çerçevesinde aynıdır: sistemlere kim, hangi kimlikle ve hangi doğrulamayla girdi; ayrıcalıklı hesaplar kimin elinde; dışarıdan gelen tedarikçi neye, ne kadar süreyle, kimin onayıyla ulaştı; olay anında kanıt nerede? Bu soruların cevabı politika dokümanıyla değil, erişim yolunun kendisinde uygulanan ve log üreten kontrollerle verilir. Bu kontrolleri bugün kuran kurum, ikincil mevzuat netleştiğinde uyum çalışmasına kanıtlarıyla başlar.'
  ),
  requirementsHeading: tr('Yükümlülük alanından kontrole, eşlenmiş.'),
  requirementsBody: tr(
    'Kanunun getirdiği yükümlülük alanlarının kimlik ve erişim boyutu ve her birini uygulayan Monofor yeteneği. Madde eşlemesi iddiası değil, hazırlık haritasıdır.'
  ),
  requirements: [
    {
      requirement: tr('Siber güvenlik tedbirlerini alma: kimlik doğrulama'),
      detail: tr(
        'Tedbir yükümlülüğünün ilk adımı, sistemlere girişte güçlü kimlik doğrulamadır.'
      ),
      capability: tr(
        'Monosign her girişte MFA uygular: TOTP, push, SMS ve oltalamaya dirençli FIDO2 passkey; yeni cihaz veya olağan dışı konum gibi risk sinyallerinde uyarlanabilir ek doğrulama devreye girer.'
      ),
      productHref: '/sign/mfa',
      productName: 'Monosign MFA',
    },
    {
      requirement: tr('Ayrıcalıklı ve idari erişimin kontrolü'),
      detail: tr(
        'Olay raporlarının tekrar tekrar bulduğu yol, yönetici erişimidir.'
      ),
      capability: tr(
        'Monopam ayrıcalıklı kimlik bilgilerini kasada tutar, yükseltme için onay ister, erişimi süreli pencerelere bağlar ve her oturumu video ve tuş vuruşu dökümüyle kaydeder.'
      ),
      productHref: '/pam',
      productName: 'Monopam',
    },
    {
      requirement: tr('Üçüncü taraf ve tedarikçi erişimi'),
      detail: tr(
        'Kritik altyapıya dışarıdan ulaşan bakımcı ve entegratörler, işletmecinin sorumluluğundadır.'
      ),
      capability: tr(
        'Tedarikçi erişimi bir yaşam döngüsü olarak yönetilir: onay kapılı davet, MFA ile parolasız kayıt, iki katmanda uygulanan süre dolumu, her bağlantıda kayıt ve sponsor bildirimi. VPN hesabı ve ortak parola ortadan kalkar.'
      ),
      productHref: '/pam/vendor-access',
      productName: 'Tedarikçi Ayrıcalıklı Erişimi',
    },
    {
      requirement: tr('Olay bildirimi ve kanıt üretimi'),
      detail: tr(
        'Bildirim süreleri, olayın kim-ne-ne zaman boyutunu hızla kurabilmeyi varsayar.'
      ),
      capability: tr(
        'Kimlik doğrulama logları, yetki geçmişi ve oturum kayıtları SIEM aktarımıyla olay ekibinin önüne gelir; ayrıcalıklı oturumlar kişiye bağlı video ve döküm olarak yeniden izlenebilir.'
      ),
      productHref: '/pam/session-recording',
      productName: 'Oturum kaydı',
    },
    {
      requirement: tr('Kapalı ağ ve veri yerliliği beklentileri'),
      detail: tr(
        'Kamu ve enerji tarafında erişim altyapısının yurt içinde ve kapalı ağda çalışması beklenir.'
      ),
      capability: tr(
        'Monofor kendi altyapınızda, gerekirse tamamen kapalı ağda kurulur; erişim yolunda SaaS veya dışa bulut bağımlılığı yoktur. Aynı ürün, aynı fiyat, sizin veri merkezinizde.'
      ),
      productHref: '/solutions/kamu-kimlik-yonetimi',
      productName: 'Kamu için kimlik yönetimi',
    },
    {
      requirement: tr('Denetime hazırlık ve düzenli gözden geçirme'),
      detail: tr(
        'Denetim yetkisi, erişimlerin düzenli gözden geçirildiğini gösterebilmeyi gerektirir.'
      ),
      capability: tr(
        'Erişim gözden geçirme kampanyaları kimin hâlâ erişime ihtiyacı olduğunu onaylatır; ret hesabı kapatır. Tedarikçi aktivite raporları ve değiştirilemez denetim izi, denetim günü değil her gün hazırdır.'
      ),
      productHref: '/solutions/audit-compliance',
      productName: 'Denetim ve uyum',
    },
  ],
  faqs: [
    {
      q: tr('7545 bize uygulanır mı?'),
      a: tr(
        'Kanun kamu kurum ve kuruluşlarını ve kritik altyapı olarak belirlenen sektörlerdeki işletmecileri kapsar; kapsamın ayrıntıları ikincil mevzuatla belirleniyor. Kuruluşunuzun hangi yükümlülüklere tabi olduğunu hukuk ekibinizle ve ilgili sektörel düzenlemelerle birlikte teyit edin.'
      ),
    },
    {
      q: tr('Monofor kurmak 7545 uyumu sağlar mı?'),
      a: tr(
        'Hiçbir ürün tek başına kanuni uyum sağlamaz; kanun yönetişim, olay yönetimi ve sektörel yükümlülükler gibi daha geniş alanları da kapsar. Monofor, yükümlülüklerin kimlik ve erişim boyutunu (MFA, ayrıcalıklı erişim, tedarikçi erişimi, iz kaydı) uygulanmış ve loglanmış kontroller olarak sağlar; denetimde gösterilebilir kanıt üretir.'
      ),
    },
    {
      q: tr('İkincil mevzuat netleşmeden neden şimdi başlayalım?'),
      a: tr(
        'Çünkü kimlik doğrulama, ayrıcalıklı erişim kontrolü ve iz kaydı; NIS2, IEC 62443, KVKK ve bugüne kadar yayımlanmış her çerçevenin ortak çekirdeğidir ve yönetmeliklerin bu çekirdeğin dışına düşme ihtimali düşüktür. Bu kontrolleri kurmak aylar süren bir programdır; bugün başlayan kurum, mevzuat netleştiğinde geriye dönük kanıt üretmeye çalışmaz, birikmiş kanıtla gelir.'
      ),
    },
    {
      q: tr('Enerji ve üretim tarafında OT sistemlerimiz var. Bu kapsama girer mi?'),
      a: tr(
        'Kritik altyapı tartışmasının merkezinde OT ortamları var. Monopam gateway endüstriyel DMZ içine, tamamen kapalı ağda kurulabilir; tedarikçi oturumları kontrol ağına girmeden DMZ içinde sonlanır ve kaydedilir. OT tarafındaki teknik çerçeve için IEC 62443 sayfamıza ve OT tedarikçi erişimi rehberimize bakın.'
      ),
    },
  ],
}

type Props = { locale: string }

export default function SiberGuvenlikKanunuPage({ locale }: Props) {
  return <CompliancePage content={content} locale={locale} />
}

// TR-only: 7545 yalnızca Türkiye pazarına hitap eder; EN locale 404.
export async function getStaticProps({ locale }: { locale: string }) {
  if (locale !== 'tr') {
    return { notFound: true }
  }
  return { props: { locale: 'tr' } }
}
